import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save,
  Shield,
  Plane,
  CalendarClock,
  Bell,
  Loader2,
  CheckCircle2,
  RotateCcw,
  Settings2,
  AlertTriangle,
  Trash2,
  ClipboardList,
  X,
} from 'lucide-react';
import { apiGet, apiPut, apiPost } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { API_ROUTES } from '@avgrp/shared';

// ─── Types ──────────────────────────────────────────────────

interface SettingsGroup {
  key: string;
  label: string;
  icon: React.ElementType;
  settings: Setting[];
}

interface Setting {
  key: string;
  label: string;
  description: string;
  type: 'text' | 'number' | 'boolean' | 'select';
  value: string | number | boolean;
  options?: { label: string; value: string }[];
  unit?: string;
}

// ─── API setting record ──────────────────────────────────────

interface ApiSetting {
  key: string;
  value: unknown;
  category: string;
  description: string | null;
}

// ─── Icon map for dynamic icon resolution ───────────────────

const iconMap: Record<string, React.ElementType> = {
  auth: Shield,
  operations: Plane,
  scheduler: CalendarClock,
  notifications: Bell,
};

// ─── Default settings schema ────────────────────────────────

const defaultSettingsSchema: Omit<SettingsGroup, 'icon'>[] = [
  {
    key: 'operations',
    label: 'Operations',
    settings: [
      { key: 'ops.turnaround_buffer_minutes', label: 'Turnaround Buffer', description: 'Extra buffer time between flights in minutes', type: 'number', value: 15, unit: 'min' },
      { key: 'ops.max_daily_flight_hours', label: 'Max Daily Flight Hours', description: 'Maximum flight hours per crew member per day', type: 'number', value: 10, unit: 'hours' },
      { key: 'ops.auto_assign_gates', label: 'Auto-Assign Gates', description: 'Automatically assign gates to arriving flights', type: 'boolean', value: true },
    ],
  },
  {
    key: 'scheduler',
    label: 'Scheduler',
    settings: [
      { key: 'scheduler.algorithm', label: 'Scheduling Algorithm', description: 'Algorithm used for automatic scheduling', type: 'select', value: 'greedy', options: [{ label: 'Greedy', value: 'greedy' }, { label: 'Balanced', value: 'balanced' }, { label: 'Cost-Optimized', value: 'cost_optimized' }] },
      { key: 'scheduler.max_tasks_per_employee', label: 'Max Tasks Per Employee', description: 'Maximum number of tasks assigned to a single employee per shift', type: 'number', value: 8 },
      { key: 'scheduler.allow_overtime', label: 'Allow Overtime', description: 'Allow tasks to extend beyond scheduled shift end', type: 'boolean', value: false },
    ],
  },
  {
    key: 'notifications',
    label: 'Notifications',
    settings: [
      { key: 'notifications.email_enabled', label: 'Email Notifications', description: 'Send email notifications for schedule changes', type: 'boolean', value: true },
      { key: 'notifications.sms_enabled', label: 'SMS Notifications', description: 'Send SMS alerts for critical changes', type: 'boolean', value: false },
      { key: 'notifications.advance_notice_hours', label: 'Advance Notice', description: 'Hours of advance notice before shift changes', type: 'number', value: 24, unit: 'hours' },
    ],
  },
  {
    key: 'auth',
    label: 'Authentication',
    settings: [
      { key: 'auth.session_timeout_minutes', label: 'Session Timeout', description: 'Auto-logout after inactivity period', type: 'number', value: 60, unit: 'min' },
      { key: 'auth.require_2fa', label: 'Require 2FA', description: 'Require two-factor authentication for all users', type: 'boolean', value: false },
    ],
  },
];

// ─── Confirmation Dialog ────────────────────────────────────

function ConfirmDialog({
  open,
  title,
  description,
  confirmText,
  confirmPhrase,
  isPending,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmText: string;
  confirmPhrase: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-red-100 p-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="mt-1 text-sm text-gray-500">{description}</p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">
            Type <span className="font-mono text-red-600">{confirmPhrase}</span> to confirm
          </label>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmPhrase}
            className="input-field mt-1"
            autoFocus
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="btn-secondary" disabled={isPending}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={typed !== confirmPhrase || isPending}
            className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────

export function SettingsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [localSettings, setLocalSettings] = useState<SettingsGroup[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [savedMessage, setSavedMessage] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [resetFlightsOpen, setResetFlightsOpen] = useState(false);
  const [resetTasksOpen, setResetTasksOpen] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);

  const { data: serverSettings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiGet<{ data: ApiSetting[] }>(API_ROUTES.SETTINGS),
    select: (res) => res.data,
  });

  // Merge server values into the default schema — re-merge whenever server data changes
  useEffect(() => {
    if (serverSettings) {
      const valueMap = new Map<string, unknown>();
      for (const s of serverSettings) {
        valueMap.set(s.key, s.value);
      }
      const merged = defaultSettingsSchema.map((group) => ({
        ...group,
        icon: iconMap[group.key] || Settings2,
        settings: group.settings.map((s) => ({
          ...s,
          value: valueMap.has(s.key) ? (valueMap.get(s.key) as typeof s.value) : s.value,
        })),
      }));
      setLocalSettings(merged);
      setHasChanges(false);
    } else if (!serverSettings && !isLoading && localSettings.length === 0) {
      // No server settings yet — show defaults
      const defaults = defaultSettingsSchema.map((group) => ({
        ...group,
        icon: iconMap[group.key] || Settings2,
      }));
      setLocalSettings(defaults);
    }
  }, [serverSettings, isLoading]);

  const saveMutation = useMutation({
    mutationFn: async (settings: SettingsGroup[]) => {
      // Save each setting individually via PUT /api/settings/:key
      const promises = settings.flatMap((group) =>
        group.settings.map((s) =>
          apiPut(`${API_ROUTES.SETTINGS}/${s.key}`, {
            value: s.value,
            description: s.description,
            category: group.key,
          })
        )
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setHasChanges(false);
      setSaveError(null);
      setSavedMessage(true);
      setTimeout(() => setSavedMessage(false), 3000);
    },
    onError: (err: any) => {
      setSaveError(err?.response?.data?.error || err?.message || 'Failed to save settings');
    },
  });

  const resetFlightsMutation = useMutation({
    mutationFn: () =>
      apiPost<{ data: { deletedFlights: number; deletedTasks: number; deletedAssignments: number } }>(
        `${API_ROUTES.SETTINGS}/reset-flights`,
        {}
      ),
    onSuccess: (res) => {
      const d = res.data;
      setResetFlightsOpen(false);
      setResetResult(`Reset complete: ${d.deletedFlights} flights, ${d.deletedTasks} tasks, ${d.deletedAssignments} assignments deleted`);
      setTimeout(() => setResetResult(null), 5000);
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      queryClient.invalidateQueries({ queryKey: ['gantt-flights'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err: any) => {
      setSaveError(err?.response?.data?.error || err?.message || 'Failed to reset flights');
      setResetFlightsOpen(false);
    },
  });

  const resetTasksMutation = useMutation({
    mutationFn: () =>
      apiPost<{ data: { deletedTasks: number; deletedAssignments: number } }>(
        `${API_ROUTES.SETTINGS}/reset-tasks`,
        {}
      ),
    onSuccess: (res) => {
      const d = res.data;
      setResetTasksOpen(false);
      setResetResult(`Reset complete: ${d.deletedTasks} tasks, ${d.deletedAssignments} assignments deleted`);
      setTimeout(() => setResetResult(null), 5000);
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      queryClient.invalidateQueries({ queryKey: ['gantt-flights'] });
      queryClient.invalidateQueries({ queryKey: ['scheduling'] });
    },
    onError: (err: any) => {
      setSaveError(err?.response?.data?.error || err?.message || 'Failed to reset tasks');
      setResetTasksOpen(false);
    },
  });

  function updateSetting(groupKey: string, settingKey: string, value: string | number | boolean) {
    setLocalSettings((prev) =>
      prev.map((group) => {
        if (group.key !== groupKey) return group;
        return {
          ...group,
          settings: group.settings.map((s) =>
            s.key === settingKey ? { ...s, value } : s,
          ),
        };
      }),
    );
    setHasChanges(true);
  }

  function handleReset() {
    const valueMap = new Map<string, unknown>();
    if (serverSettings) {
      for (const s of serverSettings) {
        valueMap.set(s.key, s.value);
      }
    }
    const merged = defaultSettingsSchema.map((group) => ({
      ...group,
      icon: iconMap[group.key] || Settings2,
      settings: group.settings.map((s) => ({
        ...s,
        value: valueMap.has(s.key) ? (valueMap.get(s.key) as typeof s.value) : s.value,
      })),
    }));
    setLocalSettings(merged);
    setHasChanges(false);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure system behavior and operational parameters
          </p>
        </div>
        <div className="flex items-center gap-3">
          {savedMessage && (
            <div className="flex items-center gap-1.5 text-sm text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              Settings saved
            </div>
          )}
          <button
            onClick={handleReset}
            disabled={!hasChanges}
            className="btn-secondary"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </button>
          <button
            onClick={() => saveMutation.mutate(localSettings)}
            disabled={!hasChanges || saveMutation.isPending}
            className="btn-primary"
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {saveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{saveError}</span>
          <button onClick={() => setSaveError(null)} className="ml-2 text-red-500 hover:text-red-700">&#10005;</button>
        </div>
      )}

      {resetResult && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {resetResult}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-aviation-200 border-t-aviation-600" />
        </div>
      ) : localSettings.length === 0 ? (
        <div className="card py-16 text-center">
          <Settings2 className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-semibold text-gray-700">No settings available</h3>
          <p className="mt-2 text-sm text-gray-400">Settings could not be loaded from the server.</p>
        </div>
      ) : (
        /* Settings groups */
        <div className="space-y-6">
          {localSettings.map((group) => {
            const GroupIcon = group.icon || Settings2;
            return (
              <div key={group.key} className="card">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-lg bg-aviation-50 p-2">
                    <GroupIcon className="h-5 w-5 text-aviation-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">{group.label}</h2>
                </div>

                <div className="space-y-6">
                  {group.settings.map((setting) => (
                    <div
                      key={setting.key}
                      className="flex items-start justify-between gap-8 border-b border-gray-100 pb-6 last:border-0 last:pb-0"
                    >
                      <div className="flex-1">
                        <label
                          htmlFor={`${group.key}-${setting.key}`}
                          className="text-sm font-medium text-gray-900"
                        >
                          {setting.label}
                        </label>
                        <p className="mt-0.5 text-sm text-gray-500">{setting.description}</p>
                      </div>

                      <div className="w-64 flex-shrink-0">
                        {setting.type === 'boolean' ? (
                          <button
                            type="button"
                            role="switch"
                            aria-checked={setting.value as boolean}
                            onClick={() =>
                              updateSetting(group.key, setting.key, !(setting.value as boolean))
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                              ${(setting.value as boolean) ? 'bg-aviation-600' : 'bg-gray-300'}
                            `}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${(setting.value as boolean) ? 'translate-x-6' : 'translate-x-1'}
                              `}
                            />
                          </button>
                        ) : setting.type === 'select' ? (
                          <select
                            id={`${group.key}-${setting.key}`}
                            value={setting.value as string}
                            onChange={(e) => updateSetting(group.key, setting.key, e.target.value)}
                            className="input-field"
                          >
                            {setting.options?.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : setting.type === 'number' ? (
                          <div className="flex items-center gap-2">
                            <input
                              id={`${group.key}-${setting.key}`}
                              type="number"
                              value={setting.value as number}
                              onChange={(e) =>
                                updateSetting(group.key, setting.key, Number(e.target.value))
                              }
                              className="input-field"
                              min={0}
                            />
                            {setting.unit && (
                              <span className="text-sm text-gray-400">{setting.unit}</span>
                            )}
                          </div>
                        ) : (
                          <input
                            id={`${group.key}-${setting.key}`}
                            type="text"
                            value={setting.value as string}
                            onChange={(e) => updateSetting(group.key, setting.key, e.target.value)}
                            className="input-field"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Danger Zone */}
          <div className="card border-red-200">
            <div className="mb-6 flex items-center gap-3">
              <div className="rounded-lg bg-red-50 p-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
            </div>

            <div className="space-y-4">
              {/* Reset Flights */}
              <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50/50 p-4">
                <div className="flex items-center gap-3">
                  <Plane className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Reset All Flights</p>
                    <p className="text-sm text-gray-500">
                      Permanently delete all flights, their tasks, and assignments for this base
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setResetFlightsOpen(true)}
                  className="inline-flex items-center rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Reset Flights
                </button>
              </div>

              {/* Reset Tasks */}
              <div className="flex items-center justify-between rounded-lg border border-red-100 bg-red-50/50 p-4">
                <div className="flex items-center gap-3">
                  <ClipboardList className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Reset All Tasks</p>
                    <p className="text-sm text-gray-500">
                      Permanently delete all flight tasks and assignments (flights are kept)
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setResetTasksOpen(true)}
                  className="inline-flex items-center rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Reset Tasks
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={resetFlightsOpen}
        title="Reset All Flights"
        description="This will permanently delete ALL flights, their tasks, and task assignments for this base. This action cannot be undone."
        confirmText="Delete All Flights"
        confirmPhrase="RESET FLIGHTS"
        isPending={resetFlightsMutation.isPending}
        onConfirm={() => resetFlightsMutation.mutate()}
        onCancel={() => setResetFlightsOpen(false)}
      />

      <ConfirmDialog
        open={resetTasksOpen}
        title="Reset All Tasks"
        description="This will permanently delete ALL flight tasks and task assignments for this base. Flights will be kept but their tasks will be removed. This action cannot be undone."
        confirmText="Delete All Tasks"
        confirmPhrase="RESET TASKS"
        isPending={resetTasksMutation.isPending}
        onConfirm={() => resetTasksMutation.mutate()}
        onCancel={() => setResetTasksOpen(false)}
      />
    </div>
  );
}
